// OVERLAY-BOX-STYLE-1 (bot request 2026-10-12, owner's word): every UNSELECTED
// diagram hotspot box is drawn exactly like the bot's overlay preview.
//
//   stroke   2 px ON SCREEN at every zoom, INSIDE the box (its outer edge is
//            the hotspot rect — CSS border-box semantics), solid, served color
//   fill     the same served color at 22 %
//   corners  square
//   no served color -> dashed #808080 stroke (2 px screen), 5 % white wash
//
// The selected box (OVERLAY-CLICK-HIGHLIGHT-1) and the answer-highlighted box
// are NOT drawn from here — DiagramHotspotRectV2 keeps their deployed values.
//
// How the stroke stays 2 screen px. The overlay <svg> uses viewBox 0 0 100 100
// with preserveAspectRatio="none" (1 x-unit = 1 % of image WIDTH, 1 y-unit =
// 1 % of image HEIGHT) and sits inside the viewer's CSS
// `translate(..) scale(zoom)` wrapper. So a stroke width in viewBox units is
// stretched twice: unevenly by the viewBox (vertical edges != horizontal
// edges) and then by the zoom. Here:
//   1. `vector-effect: non-scaling-stroke` takes the viewBox out: the width is
//      in the <svg>'s own CSS px, the same on all four edges.
//   2. The zoom wrapper still multiplies it, so the viewer sets ONE CSS custom
//      property per zoom level on the <svg> (boxStrokeVars) and every box
//      reads it with var(). Zooming re-renders no box in React.
//   3. An SVG stroke is centred on the edge. The width is therefore TWICE the
//      target, and a shared objectBoundingBox <clipPath> (the viewer's
//      `boxClipId`) clips each box to its own rectangle: the outer half is cut
//      away, exactly 2 screen px remain, inside the edge.

import { getServedDiagramColor } from "./diagramColorMap";

/** Visible stroke, in screen px, at every zoom. */
export const BOX_STROKE_SCREEN_PX = 2;
/** Fill alpha of a box that carries a served color. */
export const BOX_FILL_OPACITY = 0.22;
/** Stroke of a box with no served color. */
export const NO_COLOR_STROKE = "#808080";
/** "Near-invisible white wash" of a box with no served color. */
export const NO_COLOR_WASH = "#FFFFFF";
export const NO_COLOR_WASH_OPACITY = 0.05;
/** Dash and gap, screen px (CSS `dashed` draws ~3x the border width). */
export const NO_COLOR_DASH_SCREEN_PX = 6;

/** CSS custom properties the viewer sets on the overlay <svg>. */
export const BOX_STROKE_VAR = "--midas-hs-stroke";
export const BOX_DASH_VAR = "--midas-hs-dash";

const safeZoom = (zoom: number): number =>
  Number.isFinite(zoom) && zoom > 0 ? zoom : 1;

/**
 * Width (the <svg>'s CSS px) of the CENTRED stroke at this zoom. On screen it
 * is width x zoom = 2 x BOX_STROKE_SCREEN_PX; the clip leaves the inner half.
 */
export const boxStrokeWidthPx = (zoom: number): number =>
  (2 * BOX_STROKE_SCREEN_PX) / safeZoom(zoom);

/** Dash length = gap (the <svg>'s CSS px) at this zoom. */
export const boxDashPx = (zoom: number): number =>
  NO_COLOR_DASH_SCREEN_PX / safeZoom(zoom);

/** The custom properties for the overlay <svg> style, one set per zoom. */
export const boxStrokeVars = (zoom: number): Record<string, string> => ({
  [BOX_STROKE_VAR]: `${boxStrokeWidthPx(zoom)}px`,
  [BOX_DASH_VAR]: `${boxDashPx(zoom)}px`,
});

export interface UnselectedBoxPaint {
  stroke: string;
  fill: string;
  fillOpacity: number;
  dashed: boolean;
}

/**
 * Paint of an unselected box. The served color is canonical; with none (or a
 * malformed one) the box is dashed grey — the local type map is NOT used here.
 */
export const unselectedBoxPaint = (served?: string): UnselectedBoxPaint => {
  const color = getServedDiagramColor(served);
  return color
    ? { stroke: color, fill: color, fillOpacity: BOX_FILL_OPACITY, dashed: false }
    : {
        stroke: NO_COLOR_STROKE,
        fill: NO_COLOR_WASH,
        fillOpacity: NO_COLOR_WASH_OPACITY,
        dashed: true,
      };
};

/**
 * Props for the unselected box <rect>. The var() fallbacks = zoom 1, used
 * only if a box is ever drawn outside the viewer (no custom property set).
 * `clipId` = the viewer's shared objectBoundingBox clipPath.
 */
export const unselectedBoxProps = (served: string | undefined, clipId?: string) => {
  const paint = unselectedBoxPaint(served);
  return {
    fill: paint.fill,
    fillOpacity: paint.fillOpacity,
    stroke: paint.stroke,
    vectorEffect: "non-scaling-stroke",
    clipPath: clipId ? `url(#${clipId})` : undefined,
    style: {
      strokeWidth: `var(${BOX_STROKE_VAR}, ${boxStrokeWidthPx(1)}px)`,
      ...(paint.dashed
        ? { strokeDasharray: `var(${BOX_DASH_VAR}, ${boxDashPx(1)}px)` }
        : {}),
    },
    "data-box-style": paint.dashed ? "no-color" : "served",
  };
};
